import React, { useEffect, useMemo, useState } from "react";

import { Row, Col } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Button from "shared/components/Button";
import CheckBox from "shared/components/CheckBox";
import Input from "shared/components/Input";
import Message from "shared/components/Message";
import Notification from "shared/components/Notification";
import OpeningHours from "shared/components/OpeningHours";
import Popover from "shared/components/Popover";
import Space from "shared/components/Space";
import { DEFAULT_VALUES, NAME_SPACES } from "shared/locals/constants";
import { formatOpeninHours } from "utils/helpers/hours";
import { useInfo } from "utils/hooks/info";

import "./styles.scss";
const Component = ({
  loading,
  values,
  onChange,
  error,
  countriesQuery: { data: countriesData, loading: countriesoading },
  civilitiesQuery: { data: civilitiesData, loading: civilitiesLoading },
  updatable,
  geolocalized,
  onSubmit,
  onForceData,
}) => {
  const { info } = useInfo();
  const { t } = useTranslation(NAME_SPACES.ADDRESS);
  const LABELS = t("INTERVETION_ADDRESS", { returnObjects: true });
  const { t: tError } = useTranslation(NAME_SPACES.ERRORS);
  const [addressVisiblity, setAddressVisibility] = useState(!updatable);

  useEffect(() => {
    setAddressVisibility(!updatable);
  }, [updatable]);

  const civilities = useMemo(
    () =>
      civilitiesData.map(({ desc, code }) => ({ label: desc, value: code })),
    [civilitiesData],
  );
  const countries = useMemo(
    () => countriesData.map(({ desc, code }) => ({ label: desc, value: code })),
    [countriesData],
  );
  const openingHours = useMemo(
    () => formatOpeninHours(info.openHours),
    [info.openHours],
  );

  useEffect(() => {
    if (error?.statusCode === 472 || geolocalized === false) {
      Notification.warning({
        key: "ERROR",
        duration: DEFAULT_VALUES.NOTIFICATION_DURATION,
        message: tError(LABELS.NOTIFICATION_MESSAGE),
        btn: (
          <div className="containerBtn">
            <Row gutter={20}>
              <Col>
                <p
                  onClick={() => {
                    setAddressVisibility(false);
                    Notification.close("ERROR");
                  }}>
                  {LABELS.YES}
                </p>
              </Col>
              <Col>
                <p
                  onClick={() => {
                    onForceData();
                    Notification.close("ERROR");
                  }}>
                  {LABELS.NO}
                </p>
              </Col>
            </Row>
          </div>
        ),
      });
    }
  }, [error, geolocalized]);

  return (
    <div className={"page-card"}>
      <div className="sub-page-card">
        <Row>
          <p className={"title"}>{LABELS.TITLE}</p>
        </Row>
        <Space h={14} />
        <Row>
          <Message
            level={"warning"}
            message={
              <>
                {t("INTERVETION_ADDRESS.WARNING_MESSAGE", {
                  phone: info.phone,
                })}
                <Popover content={<OpeningHours data={openingHours} />}>
                  <b>{t("INTERVETION_ADDRESS.WARNING_OPENINGHOURS")}</b>
                </Popover>
              </>
            }
            visible={!updatable}
          />
          <Message
            level={"warning"}
            message={
              <>
                {t("INTERVETION_ADDRESS.GEO_WARNING_MESSAGE", {
                  phone: info.phone,
                })}
                <Popover content={<OpeningHours data={openingHours} />}>
                  <b>{t("INTERVETION_ADDRESS.WARNING_OPENINGHOURS")}</b>
                </Popover>
              </>
            }
            visible={geolocalized === false}
          />
        </Row>

        <Space h={24} />

        <Row gutter={[16, 24]}>
          <Col className="column-width" sm={24} md={12}>
            <Row gutter={[16, 24]}>
              <Col className="column-width" sm={24} md={8}>
                <Input
                  mode="select"
                  placeholder={LABELS.CIVILITY}
                  items={civilities}
                  loading={civilitiesLoading}
                  onSelect={(civilityCode) =>
                    onChange({ ...values, civilityCode })
                  }
                  value={values.civilityCode}
                />
              </Col>
              <Col className="column-width" sm={24} md={16}>
                <Input
                  placeholder={
                    values.civilityCode === "6"
                      ? LABELS.INTERLOCUTEUR
                      : LABELS.FIRST_NAME
                  }
                  onChange={(e) =>
                    onChange({ ...values, firstname: e.target.value })
                  }
                  value={values.firstname}
                />
              </Col>
            </Row>
          </Col>

          <Col className="column-width" sm={24} md={12}>
            <Input
              placeholder={LABELS.LAST_NAME}
              onChange={(e) => onChange({ ...values, name: e.target.value })}
              value={values.name}
            />
          </Col>
        </Row>
        <Space h={24} />
        <Row gutter={[16, 24]}>
          <Col className="column-width" sm={24} md={12}>
            <Input
              placeholder={LABELS.ADDRESS}
              onChange={(e) =>
                onChange({ ...values, address1: e.target.value })
              }
              value={values.address1}
              disabled={addressVisiblity}
            />
          </Col>
          <Col className="column-width" sm={24} md={12}>
            <Input
              placeholder={LABELS.COMPLEMENT}
              onChange={(e) =>
                onChange({ ...values, address2: e.target.value })
              }
              value={values.address2}
              disabled={!updatable}
            />
          </Col>
        </Row>
        <Space h={24} />
        <Row gutter={[16, 24]}>
          <Col className="column-width" sm={24} md={12}>
            <Row gutter={[16, 24]}>
              <Col className="column-width" sm={24} md={16}>
                <Input
                  placeholder={LABELS.CITY}
                  onChange={(e) =>
                    onChange({ ...values, town: e.target.value })
                  }
                  value={values.town}
                  disabled={!updatable}
                />
              </Col>
              <Col className="column-width" sm={24} md={8}>
                <Input
                  placeholder={LABELS.POSTAL_CODE}
                  onChange={(e) =>
                    onChange({ ...values, postCode: e.target.value })
                  }
                  value={values.postCode}
                  disabled={!updatable}
                />
              </Col>
            </Row>
          </Col>
          <Col className="column-width" sm={24} md={12}>
            <Input
              mode="select"
              placeholder={LABELS.COUNTRY}
              items={countries}
              loading={countriesoading}
              onSelect={(countryCode) => onChange({ ...values, countryCode })}
              value={values.countryCode}
              disabled={!updatable}
            />
          </Col>
        </Row>
        <Space h={24} />
        {/* <Row gutter={6}>
        <Col span={12}>
          <Input
            placeholder={LABELS.EMAIL}
            onChange={(e) => onChange({ ...values, email: e.target.value })}
            value={values.email}

          />
        </Col>
        <Col span={12}>
          <Input
            placeholder={LABELS.PHONE}
            onChange={(e) => onChange({ ...values, phone: e.target.value })}
            value={values.phone}

          />
        </Col>
      </Row>
      <Space h={24} /> */}

        <Row gutter={[16, 24]}>
          <Col className="column-width" sm={24} md={12}>
            <CheckBox
              label={LABELS.CHECKBOX}
              onChange={(e) =>
                onChange({ ...values, updateAccount: e.target.checked })
              }
              checked={values.updateAccount}
              disabled={!updatable}
            />
          </Col>
          <Col sm={24} className="containerButton" md={12}>
            <Button
              loading={loading}
              text={LABELS.SAVE}
              variant={"SECONDARY"}
              onClick={onSubmit}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

Component.propTypes = {
  loading: PropTypes.bool.isRequired,
  countriesQuery: PropTypes.shape({
    data: PropTypes.arrayOf(
      PropTypes.shape({
        desc: PropTypes.string.isRequired,
        code: PropTypes.string.isRequired,
      }),
    ).isRequired,
    loading: PropTypes.bool.isRequired,
  }).isRequired,
  civilitiesQuery: PropTypes.shape({
    data: PropTypes.arrayOf(
      PropTypes.shape({
        desc: PropTypes.string.isRequired,
        code: PropTypes.string.isRequired,
      }),
    ).isRequired,
    loading: PropTypes.bool.isRequired,
  }).isRequired,
  values: PropTypes.shape({
    civilityCode: PropTypes.string.isRequired,
    firstname: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    address1: PropTypes.string.isRequired,
    address2: PropTypes.string.isRequired,
    town: PropTypes.string.isRequired,
    postCode: PropTypes.string.isRequired,
    countryCode: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    updateAccount: PropTypes.bool.isRequired,
  }).isRequired,
  error: PropTypes.shape({
    statusCode: PropTypes.number.isRequired,
  }),
  onChange: PropTypes.func.isRequired,
  updatable: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  geolocalized: PropTypes.bool.isRequired,
  onForceData: PropTypes.func.isRequired,
};

export default Component;
